import React from 'react';
import PropTypes from 'prop-types';
import querier from '../../utilities/querier';
import { formatPhoneNumberForDisplay } from '../../utilities/formatPhoneNumber';
import ErrorBox from './ErrorBox';
import stateSelectOptions from './constants/stateSelectOptions';

import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

const DarkBorderTextField = styled(TextField)(({ theme }) => ({
	'& label.Mui-focused': {
		color: theme.palette.common.black
	}
}));

const CheckoutForm = (props) => {
	const { checkoutInformation, handleUpdateForm, submitErrors } = props;

	return (
		<FormControl
			className='filter'
			sx={{
				width: { xs: '96%', md: '60%', lg: '51%', xl: '67%' },
				ml: { xs: '0', md: '100px' },
				background: (theme) => theme.palette.common.white
			}}
		>
			<ErrorBox submitErrors={submitErrors} />
			<Typography variant='h5' sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' }, px: 1 }}>
				Billing Details
			</Typography>
			<Stack direction='row' mt={2} sx={{ gap: '15px' }}>
				<Stack sx={{ flex: 1 }}>
					<DarkBorderTextField
						required
						id='first-name-input'
						label='First Name'
						fullWidth
						onChange={(e) => handleUpdateForm('firstName', e.target.value)}
						value={checkoutInformation.firstName}
						error={querier(submitErrors, '$[type = "firstName"]') && true}
					/>
				</Stack>
				<Stack sx={{ flex: 1 }}>
					<DarkBorderTextField
						required
						id='last-name-input'
						sx={{
							mr: 0
						}}
						label='Last Name'
						fullWidth
						onChange={(e) => handleUpdateForm('lastName', e.target.value)}
						value={checkoutInformation.lastName}
					/>
				</Stack>
			</Stack>
			<DarkBorderTextField
				id='company-name-input'
				label='Company Name (optional)'
				fullWidth
				onChange={(e) => handleUpdateForm('company', e.target.value)}
				value={checkoutInformation.company}
			/>
			<DarkBorderTextField
				required
				id='street-address-input'
				label='Street Address'
				fullWidth
				onChange={(e) => handleUpdateForm('address', e.target.value)}
				value={checkoutInformation.address}
			/>
			<Stack
				className='togglebg'
				sx={{ flexDirection: { xs: 'column', md: 'row' }, gap: { xs: '0', md: '15px' } }}
			>
				<Stack sx={{ flex: 1 }}>
					<DarkBorderTextField
						required
						id='town-city-input'
						label='City'
						fullWidth
						onChange={(e) => handleUpdateForm('city', e.target.value)}
						value={checkoutInformation.city}
					/>
				</Stack>
				<Stack sx={{ flex: 0.5 }}>
					<InputLabel htmlFor='state-input' sx={{ display: 'none' }}>
						State
					</InputLabel>
					<DarkBorderTextField
						required
						id='state-input'
						label='State'
						fullWidth
						inputProps={{ id: 'state-input' }}
						select
						onChange={(e) => handleUpdateForm('state', e.target.value)}
						value={checkoutInformation.state}
					>
						{stateSelectOptions.map((state) => {
							return (
								<MenuItem
									key={state.abbreviation}
									value={state.abbreviation}
									disabled={state.disabled}
								>
									{state.name}
								</MenuItem>
							);
						})}
					</DarkBorderTextField>
				</Stack>
				<Stack sx={{ flex: 0.5 }}>
					<DarkBorderTextField
						required
						id='zip-code-input'
						label='Zip Code'
						fullWidth
						onChange={(e) => handleUpdateForm('zip', e.target.value)}
						value={checkoutInformation.zip}
						inputProps={{
							inputMode: 'numeric',
							maxLength: 5
						}}
					/>
				</Stack>
			</Stack>
			<DarkBorderTextField
				required
				id='phone-input'
				label='Phone Number'
				fullWidth
				onChange={(e) => handleUpdateForm('phoneNumber', e.target.value)}
				value={formatPhoneNumberForDisplay(checkoutInformation.phoneNumber)}
			/>
			<DarkBorderTextField
				required
				id='email-input'
				label='Email Address'
				fullWidth
				onChange={(e) => handleUpdateForm('email', e.target.value)}
				value={checkoutInformation.email}
			/>
			<FormControlLabel
				id='optional-physical-gift-card-checkbox'
				control={<Checkbox />}
				label='Gift Cards are fulfilled electronically. Check this box for Physical Gift Cards (Physical Gift Cards are shipped for free, and arrive in 7-10 days)'
				sx={{ ml: 0, mb: '30px', mt: '15px' }}
				value={checkoutInformation.electronicFulfillment}
				onChange={() =>
					handleUpdateForm('electronicFulfillment', !checkoutInformation.electronicFulfillment)
				}
			/>
			<Typography variant='h5' sx={{ my: 1, fontSize: { xs: '1.2rem', md: '1.5rem' }, px: 1 }}>
				Additional Information
			</Typography>
			<Typography sx={{ px: 1 }}>
				<FormLabel htmlFor='order-notes'>Order notes (optional)</FormLabel>
			</Typography>
			<DarkBorderTextField
				multiline
				rows={1}
				id='order-notes'
				inputProps={{ id: 'order-notes' }}
				sx={{ mt: 2 }}
				placeholder='Notes for delivery'
				value={checkoutInformation.additionalNotes}
				onChange={(e) => handleUpdateForm('additionalNotes', e.target.value)}
			/>
		</FormControl>
	);
};

CheckoutForm.propTypes = {
	checkoutInformation: PropTypes.object,
	handleUpdateForm: PropTypes.func,
	submitErrors: PropTypes.array
};

export default CheckoutForm;
